import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import "./styles/theme.css";
import "./styles/global.css";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ScrollRestoration from "./components/ScrollRestoration";
import LoadingFallback from "./components/LoadingFallback";
import { ThemeProvider } from "./context/ThemeContext";

// Lazy load components for better performance
const HomePage = lazy(() => import("./components/HomePage"));
const Timeline = lazy(() => import("./components/Timeline"));
const EarlyLife = lazy(() => import("./components/EarlyLife"));
const InternationalEducation = lazy(() => import("./components/InternationalEducation"));
const SocialReform = lazy(() => import("./components/SocialReform"));
const ConstitutionalContributions = lazy(() => import("./components/ConstitutionalContributions"));
const PoliticalCareer = lazy(() => import("./components/PoliticalCareer"));
const PhilosophicalTeachings = lazy(() => import("./components/PhilosophicalTeachings"));
const MahadSatyagraha = lazy(() => import("./components/MahadSatyagraha"));
const BurningOfManusmriti = lazy(() => import("./components/BurningOfManusmriti"));
const NotFound = lazy(() => import("./components/NotFound"));

function App() {
  // Add a class to the body to prevent scrollbar flashing during animations
  useEffect(() => {
    // Add the class when the component mounts
    document.body.classList.add("prevent-scroll-flash");

    // Clean up function to remove the class when the component unmounts
    return () => {
      document.body.classList.remove("prevent-scroll-flash");
    };
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <Router>
          <ScrollRestoration />
          <div className="App">
            <Navbar />
            <div className="content-container">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/dr-babasaheb-ambedkar-timeline" element={<Timeline />} />
                  <Route path="/dr-ambedkar-early-life-education" element={<EarlyLife />} />
                  <Route
                    path="/dr-ambedkar-international-education-columbia-lse"
                    element={<InternationalEducation />}
                  />
                  <Route path="/dr-ambedkar-social-reform-movement" element={<SocialReform />} />
                  <Route path="/mahad-satyagraha-dr-ambedkar-water-rights" element={<MahadSatyagraha />} />
                  <Route
                    path="/burning-manusmriti-dr-ambedkar-social-revolution"
                    element={<BurningOfManusmriti />}
                  />
                  <Route
                    path="/dr-ambedkar-constitution-architect-contributions"
                    element={<ConstitutionalContributions />}
                  />
                  <Route path="/dr-ambedkar-political-career-leadership" element={<PoliticalCareer />} />
                  <Route
                    path="/dr-ambedkar-philosophy-teachings-buddhism"
                    element={<PhilosophicalTeachings />}
                  />
                  {/* Canonical redirects for legacy URLs to prevent SEO duplicate indexing */}
                  <Route path="/timeline" element={<Navigate to="/dr-babasaheb-ambedkar-timeline" replace />} />
                  <Route path="/early-life" element={<Navigate to="/dr-ambedkar-early-life-education" replace />} />
                  <Route path="/international-education" element={<Navigate to="/dr-ambedkar-international-education-columbia-lse" replace />} />
                  <Route path="/social-reform" element={<Navigate to="/dr-ambedkar-social-reform-movement" replace />} />
                  <Route path="/mahad-satyagraha" element={<Navigate to="/mahad-satyagraha-dr-ambedkar-water-rights" replace />} />
                  <Route path="/burning-of-manusmriti" element={<Navigate to="/burning-manusmriti-dr-ambedkar-social-revolution" replace />} />
                  <Route path="/constitutional-contributions" element={<Navigate to="/dr-ambedkar-constitution-architect-contributions" replace />} />
                  <Route path="/political-career" element={<Navigate to="/dr-ambedkar-political-career-leadership" replace />} />
                  <Route path="/philosophical-teachings" element={<Navigate to="/dr-ambedkar-philosophy-teachings-buddhism" replace />} />
                  {/* Catch-all 404 handler */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </div>
            <Footer />
            <ScrollToTop />
          </div>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
